import { useMemo } from "react";

import useAppDispatch from "../store/useAppDispatch";
import useAppSelector from "../store/useAppSelector";
import useEvent from "../useEvent";
import { useTranslation } from "react-i18next";
import { GAME_TYPE } from "constants/game.constants.js";
import ToastManager from "helpers/toastManager";
import { selectAssignedGameTypes } from "store/slices/auth/selectors.js";
import { binaryToFlags } from "utils/binaryCalculations.js";
import isNullOrUndefined from "utils/typeChecks/isNullOrUndefined.js";
import isEmptyArray from "utils/typeChecks/isEmptyArray.js";
import { activateBonusThunk } from "store/slices/bonus/thunks.js";

const validGameTypes = Object.values(GAME_TYPE);

const useBonusActivation = ({ gameType, bonus }) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const assignedGameTypes = useAppSelector(selectAssignedGameTypes);
	const invalidBonus = isNullOrUndefined(bonus);
	const invalidGameType = !validGameTypes.includes(gameType);

	const { hasError, message, isKey } = useMemo(() => {
		const retVal = { hasError: false, message: null, isKey: false };

		if (invalidBonus) {
			retVal.hasError = true;
			retVal.isKey = false;
			retVal.message = "Invalid Bonus data";
			return retVal;
		}

		if (invalidGameType) {
			retVal.hasError = true;
			retVal.isKey = false;
			retVal.message = "Invalid Game Type";
			return retVal;
		}

		const allowedGameTypes = binaryToFlags(assignedGameTypes, bonus.gameType);

		if (isEmptyArray(allowedGameTypes)) {
			retVal.hasError = true;
			retVal.isKey = false;
			retVal.message = "Empty Games";
			return retVal;
		}

		return retVal;
	}, [gameType, assignedGameTypes, bonus, invalidGameType, invalidBonus, t]);

	const activateBonus = useEvent(async() => {
		if (hasError) {
			return;
		}

		await dispatch(activateBonusThunk(true));

		ToastManager.instance.success(t("bonus.bonusIsNowActive"))
	});

	const deactivateBonus = useEvent(() => {
		if (hasError) {
			return;
		}
		// dispatch(clearActivateBonusAction());
	});

	return {
		hasError,
		message,
		isKey,
		activateBonus,
		deactivateBonus,
		assignedGameTypes,
		invalidBonus,
		invalidGameType
	};
};

export default useBonusActivation;
